import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  cn,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
} from "@salestrak/ui";
import { useActivityContext } from "./activity-context";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import { useAppData } from "#app/lib/data";
import { formatNoteDate } from "#app/lib/utils/format-date";
import { EntityTag, EntityGroupTag, type EntityData } from "#app/components/entity";
import { NoteContent } from "./note-content";
import { NoteComposer } from "./note-composer";
import { NoteMediaDisplay } from "./note-media-attachments";
import {
  MediaLightbox,
  type LightboxMediaItem,
} from "#app/components/media/media-lightbox";
import {
  mediaIdFromContentUrl,
  mediaIdFromObjectUrl,
  mediaIdsFromHtml,
} from "#app/lib/media-content";
import { mediaKind } from "#app/lib/media-kind";
import { NoteReactions } from "./note-reactions";
import { DeleteNoteDialog } from "./delete-note-dialog";
import type { NoteNode, ActivityAuthor } from "./activity-types";
import type { Omittable } from "@salestrak/ui";

// 24px action-button row + paddingY="sm" (8px each); pinned so the header
// doesn't reflow when its right side swaps between date and action buttons.
const NOTE_HEADER_HEIGHT = 40;

type NoteItemProps = Omittable<{
  issueId: string;
  noteId: string;
  isParent?: boolean;
  headerExtra?: React.ReactNode;
  onRequestReply?: () => void;
  onExpandThread?: () => void;
  replyInfo?: {
    count: number;
    authors: ActivityAuthor[];
  } | null;
  showFooterBorder?: boolean;
}>;

export const NoteItem = React.memo(function NoteItem({
  issueId,
  noteId,
  isParent = false,
  headerExtra,
  onRequestReply,
  onExpandThread,
  replyInfo = null,
  showFooterBorder = false,
}: NoteItemProps) {
  const {
    getNote,
    toggleReaction,
    deleteNote,
    selectedNoteId,
    registerNoteRef,
    markNoteRead,
  } = useActivityContext("NoteItem");
  const { currentUser } = useAppData();
  const note = getNote(noteId);
  // View-only viewers can't comment, so reply / edit (which produce
  // new notes) must be hidden too.
  const { canComment } = useIssueViewCapabilities();
  // Backend-resolved per-viewer flags — mirror the @RequireAccess
  // gate (comment-tier access on the parent issue + ownership OR
  // manage.users). Falling back to false during loading so disabled
  // controls don't briefly flash editable.
  const canEdit = !!note?.viewerCanEdit;
  const canDelete = !!note?.viewerCanDelete;
  const isHighlighted = selectedNoteId === noteId;

  const elRef = useRef<HTMLDivElement | null>(null);
  const cardRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      registerNoteRef(noteId, el);
    },
    [noteId, registerNoteRef],
  );

  // Slack-style: mark this note read once it actually scrolls into view.
  // Only unread notes are observed; the observer fires once then disconnects.
  //
  // We can't use a ratio threshold (e.g. 0.5): a note taller than ~2x the
  // viewport can never reach 50% visibility and would never be marked read.
  // So the trigger stays geometric (threshold 0 + a negative rootMargin) but
  // shrinks ONLY the top — a note counts as "seen" once it clears the top 30%
  // of the viewport. A symmetric band (shrinking the bottom too) leaves a dead
  // zone at the bottom edge, which is exactly where this bottom-anchored feed
  // pins the newest — and most-likely-unread — notes: they sit below the band
  // on open and never get marked unless the user happens to scroll them up.
  // Excluding only the top still ignores an older note briefly peeking in from
  // the top while you read at the bottom. IntersectionObserver already clips
  // against ancestor scroll containers, so a note scrolled out of an overflow
  // region (e.g. a mobile drawer) does not register even with the viewport as
  // root.
  const isUnread = !!note?.unread;
  useEffect(() => {
    if (!isUnread) return;
    const el = elRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          markNoteRead(noteId);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: "-30% 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isUnread, noteId, markNoteRead]);

  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Fullscreen viewer — footer tiles open over the attachment list, inline
  // images over the note body's inline photo set.
  const [lightbox, setLightbox] = useState<{
    media: LightboxMediaItem[];
    index: number;
  } | null>(null);

  const showActions = isHovered || isMenuOpen;

  const handleToggleReaction = useCallback(
    (emoji: string) => {
      toggleReaction(noteId, emoji);
    },
    [toggleReaction, noteId],
  );

  const handleDelete = useCallback(async () => {
    await deleteNote(noteId);
  }, [deleteNote, noteId]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  if (!note) return null;

  const author = note.author;
  const currentUserId = currentUser?.userId ?? null;
  const footerMedia = note.media ?? [];
  const handleInlineImageClick = (src: string) => {
    // Rendered inline imgs carry session object URLs; unresolved ones carry
    // the canonical content-route src — which the DOM absolutizes against
    // the APP origin, so also try the bare pathname.
    const photoId =
      mediaIdFromObjectUrl(src) ??
      mediaIdFromContentUrl(src) ??
      mediaIdFromContentUrl(new URL(src, window.location.origin).pathname);
    if (!photoId) return;
    const ids = mediaIdsFromHtml(note.note);
    if (!ids.length) return;
    // Inline note media are always images.
    setLightbox({
      media: ids.map((id) => ({ id, contentType: "image/*" })),
      index: Math.max(0, ids.indexOf(photoId)),
    });
  };

  return (
    <>
      <Box ref={cardRefCallback}>
        <Card
          className={cn(
            "overflow-hidden",
            // Square bottom while the thread continues below — the reply
            // region reads as the same card. (A per-side override the
            // `radius` prop cannot express; the seam hides the ring edge.)
            isParent && !showFooterBorder && "rounded-b-none",
            isHighlighted && "border-primary",
          )}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => setIsHovered(false)}
        >
          <Box
            direction="row"
            align="center"
            justify="between"
            paddingX="md"
            paddingY="sm"
            height={NOTE_HEADER_HEIGHT}
          >
            <Box direction="row" align="center" shrink>
              <EntityTag
                entity={{
                  kind: "user",
                  id: author?.userId ?? "system",
                  user: author ?? null,
                }}
                display="full"
                size="sm"
                weight="semibold"
                system={!author}
              />
            </Box>

            {!isEditing && (
              <Box direction="row" align="center" gap="xs" height={24}>
                {showActions ? (
                  <>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={handleEdit}
                        aria-label="Edit comment"
                      >
                        <Icon name="pen-line" size="xs" />
                      </Button>
                    )}
                    {isParent && canComment && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={onRequestReply}
                        aria-label="Reply"
                      >
                        <Icon name="reply" size="xs" />
                      </Button>
                    )}
                    {canDelete && (
                      <Menu onOpenChange={setIsMenuOpen}>
                        <MenuTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              aria-label="Comment actions"
                            />
                          }
                        >
                          <Icon name="more-vertical" size="xs" />
                        </MenuTrigger>
                        <MenuPopup align="end">
                          <MenuItem onClick={() => setShowDeleteDialog(true)}>
                            <Icon name="trash-2" />
                            {isParent ? "Delete thread..." : "Delete comment..."}
                          </MenuItem>
                        </MenuPopup>
                      </Menu>
                    )}
                  </>
                ) : (
                  <Text size="xs" variant="muted">
                    {formatNoteDate(note.creationDate)}
                    {note.edited && " (edited)"}
                  </Text>
                )}
              </Box>
            )}

            {isEditing && (
              <Text size="xs" variant="muted">
                {formatNoteDate(note.creationDate)}
              </Text>
            )}
          </Box>

          {headerExtra && (
            <Box paddingX="md" paddingBottom="sm">
              {headerExtra}
            </Box>
          )}

          {/* One persistent body box for read + edit with identical padding in
              both states, so entering edit only fades the border in (no layout
              shift) — the transparent border reserves the 1px either way. */}
          <Box
            marginX="sm" marginBottom="sm" transition="colors" duration={200} rounded="md"
            borderColor={isEditing ? "input" : "transparent"}
            paddingX="sm"
            paddingY="2xs"
            className="ease-in-out"
          >
            {isEditing ? (
              <NoteComposer
                issueId={issueId}
                editMode
                editNoteId={noteId}
                initialContent={note.note}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <>
                <NoteContent
                  content={note.note}
                  onImageClick={handleInlineImageClick}
                />
                {footerMedia.length > 0 ? (
                  <Box paddingBottom="2xs">
                    <NoteMediaDisplay
                      media={footerMedia}
                      onImageClick={(imageId) => {
                        // Image tiles open the lightbox over the note's image
                        // attachments (video/pdf/doc tiles have their own click
                        // actions), at the clicked image's position.
                        const imageItems = footerMedia
                          .filter((m) => mediaKind(m.contentType) === "image")
                          .map((m) => ({
                            id: m.id,
                            contentType: m.contentType,
                            fileName: m.fileName,
                          }));
                        setLightbox({
                          media: imageItems,
                          index: Math.max(
                            0,
                            imageItems.findIndex((m) => m.id === imageId),
                          ),
                        });
                      }}
                    />
                  </Box>
                ) : null}
                <MediaLightbox
                  media={lightbox?.media ?? []}
                  open={!!lightbox}
                  onOpenChange={(open) => {
                    if (!open) setLightbox(null);
                  }}
                  initialIndex={lightbox?.index ?? 0}
                />
              </>
            )}
          </Box>

          {/* Footer hosts reactions + the reply CTA. Hide it
              entirely when there's nothing to render — i.e. the
              viewer can't comment AND no existing reactions AND no
              replies to expand. Otherwise even an empty footer adds
              a dead bar below the note. */}
          {(() => {
            if (isEditing) return null;
            const hasReactions = (note.reactions?.length ?? 0) > 0;
            const showReactions = canComment || hasReactions;
            const showReplyCta = !!replyInfo && (canComment || replyInfo.count > 0);
            if (!showReactions && !showReplyCta) return null;
            return (
            <>
              {showFooterBorder && (
                <Box background="border" height={1} />
              )}
              <Box
                direction="row"
                align="center"
                justify="between"
                paddingX="md"
                paddingTop="sm"
                paddingBottom="sm"
              >
                {showReactions ? (
                  <NoteReactions
                    reactions={note.reactions ?? []}
                    currentUserId={currentUserId}
                    onToggleReaction={handleToggleReaction}
                    readOnly={!canComment}
                  />
                ) : (
                  <Box />
                )}

                {/* Reply CTA only shows when the viewer can post.
                    When there ARE replies, keep the count visible so
                    view-only viewers can still expand to read. */}
                {showReplyCta && replyInfo && (
                  <Button variant="ghost" size="xs" onClick={onExpandThread}>
                    <Text size="xs" variant="muted" weight="medium">
                      {replyInfo.count === 0
                        ? "Reply"
                        : `${replyInfo.count} ${replyInfo.count === 1 ? "reply" : "replies"}`}
                    </Text>
                    {replyInfo.count > 0 && replyInfo.authors.length > 0 && (
                      <EntityGroupTag
                        entities={replyInfo.authors.map(
                          (a, i): EntityData => ({
                            kind: "user",
                            id: a.userId ?? String(i),
                            user: a,
                          }),
                        )}
                        max={3}
                        size="xs"
                        title="Replies"
                        interactive={false}
                      />
                    )}
                  </Button>
                )}
              </Box>
            </>
            );
          })()}
        </Card>
      </Box>

      <DeleteNoteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        isParent={isParent}
        replyCount={(note as NoteNode).replyCount ?? 0}
        onConfirm={handleDelete}
      />
    </>
  );
});
